// Sentry React SDK integration (ADR-053)
// Initialized lazily — only when VITE_SENTRY_ENABLED=true and VITE_SENTRY_DSN is set.

import * as Sentry from '@sentry/react';

let _initialized = false;

/**
 * Initialize Sentry error tracking for the React frontend.
 * Call this before React renders (in main.tsx).
 */
export function initSentry(): void {
  if (_initialized) return;   // Idempotency guard — Sentry.init must only be called once
  if (import.meta.env.VITE_SENTRY_ENABLED === 'false') return;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
    // No performance tracing — error capture only (ADR-056)
    tracesSampleRate: 0,
    // Ignore errors originating from browser extensions (checks all chained exception values)
    beforeSend(event) {
      const hasExtensionFrame = event.exception?.values?.some(
        (v) => v.stacktrace?.frames?.some((f) => f.filename?.includes('extension://'))
      );
      if (hasExtensionFrame) return null;
      return event;
    },
  });
  _initialized = true;
}

/**
 * Capture an exception and forward it to Bugsink via the Sentry SDK.
 */
export function captureException(err: Error, context?: Record<string, unknown>): void {
  if (!_initialized) return;
  Sentry.withScope((scope) => {
    if (context) scope.setContext('extra', context);
    Sentry.captureException(err);
  });
}
